import {addDays, startOfDay} from 'date-fns';
import {FormField, FormControl} from 'platform/components';
import {Box, HStack, VStack, Show} from 'platform/foundation';

import {UseFormReturn, useWatch} from 'react-hook-form';

import {isNotNil, isEmpty, isNil} from 'ramda';

import {
  useGetTireOrderDestinationBranchQuery,
  useGetTireSetDestinationBranchQuery,
} from '@dms/api/core';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps, getApiDateString, Nullish} from 'shared/web';

import {useTireSetActions} from '../hooks/useTireSetActions';
import {TireSetDetailFormDefinition} from '../types/TireSetDetailFormDefinition';

interface RequestTireSetFieldsProps extends TestIdProps {
  control: FormControl<TireSetDetailFormDefinition>;
  formApi: UseFormReturn<TireSetDetailFormDefinition>;
  tireOrderId?: string;
  tireSetId?: string;
  tireSetState?: string | Nullish;
  isEdit?: boolean;
  isDraft?: boolean;
  homeBranchId: string | Nullish;
}

export function RequestTireSetFields(props: RequestTireSetFieldsProps) {
  const tireSetActions = useTireSetActions({
    tireOrderId: props.tireOrderId ?? '',
    tireSetId: props.tireSetId ?? '',
    control: props.control,
    homeBranchId: props.homeBranchId,
  });

  const requestActions = props.isDraft
    ? {
        availableActions: [],
        canRequest: isNotNil(props.tireSetState),
        canModifyRequest: isNotNil(props.tireSetState),
        canCancelRequest: isNotNil(props.tireSetState),
      }
    : {
        availableActions: tireSetActions.availableActions,
        canRequest: tireSetActions.availableActions.some(
          (action: {key: string}) => action.key === 'request'
        ),
        canModifyRequest: tireSetActions.availableActions.some(
          (action: {key: string}) => action.key === 'modifyRequest'
        ),
        canCancelRequest: tireSetActions.availableActions.some(
          (action: {key: string}) => action.key === 'cancelRequest'
        ),
      };

  const {data: tireSetDestinationBranches, isLoading: isTireSetDestinationBranchesLoading} =
    useGetTireSetDestinationBranchQuery(
      {
        orderId: props.tireOrderId || '',
        setId: props.tireSetId || '',
      },
      {
        skip: !props.tireOrderId || !props.tireSetId,
      }
    );

  const {data: tireOrderDestinationBranches, isLoading: isTireOrderDestinationBranchesLoading} =
    useGetTireOrderDestinationBranchQuery(
      {
        orderId: props.tireOrderId || '',
      },
      {
        skip: !props.tireOrderId || !!props.tireSetId,
      }
    );

  const destinationBranches = props.tireSetId
    ? tireSetDestinationBranches
    : tireOrderDestinationBranches;

  const isDestinationBranchesLoading = props.tireSetId
    ? isTireSetDestinationBranchesLoading
    : isTireOrderDestinationBranchesLoading;

  const isEnabled = useWatch({
    control: props.control,
    name: 'tireSetRequestEnabled',
    defaultValue: false,
  });

  const selectedDestinationBranchId = useWatch({
    control: props.control,
    name: 'destinationBranchId',
  });

  const handleSwitchChange = (enabled: boolean) => {
    if (!enabled || !props.formApi) {
      return;
    }

    const currentBranchId = props.formApi.getValues('destinationBranchId');
    if (currentBranchId || isNil(destinationBranches) || isEmpty(destinationBranches)) {
      return;
    }

    const defaultBranch = destinationBranches.find((branch) => branch.isDefault);
    if (isNil(defaultBranch)) {
      return;
    }

    props.formApi.setValue('destinationBranchId', defaultBranch.branchId);

    const currentNeededAtDate = props.formApi.getValues('neededAtDate');
    if (isNil(currentNeededAtDate)) {
      const minimumDate = addDays(startOfDay(new Date()), defaultBranch.transitTimeDays);
      props.formApi.setValue('neededAtDate', getApiDateString(minimumDate));
    }
  };

  const handleBranchChange = (branchId: string | number | null) => {
    if (isNil(branchId)) {
      return;
    }

    const selectedBranch = destinationBranches?.find((branch) => branch.branchId === branchId);
    if (isNil(selectedBranch)) {
      return;
    }

    const minimumDate = addDays(startOfDay(new Date()), selectedBranch.transitTimeDays);

    props.formApi.setValue('neededAtDate', getApiDateString(minimumDate));
  };

  const destinationBranchesOptions = (destinationBranches ?? []).map((branch) => ({
    label: branch.branchName,
    value: branch.branchId,
  }));

  const getDisabledDates = () => {
    const selectedBranch = destinationBranches?.find(
      (branch) => branch.branchId === selectedDestinationBranchId
    );

    if (!selectedBranch) {
      return [];
    }

    const today = startOfDay(new Date());

    const disabledDates = [];
    for (let i = 0; i < selectedBranch.transitTimeDays; i++) {
      disabledDates.push(addDays(today, i));
    }

    return disabledDates;
  };

  const canRequest = requestActions?.canRequest ?? false;
  const canModifyRequest = requestActions?.canModifyRequest ?? false;
  const canCancelRequest = requestActions?.canCancelRequest ?? false;
  const canEditFields = canRequest || canModifyRequest;
  const canToggle = canRequest || canModifyRequest || canCancelRequest;

  return (
    <VStack spacing={4}>
      <Box width="fit-content">
        <FormField
          control={props.control}
          label={i18n.t('entity.tireSet.labels.request')}
          name="tireSetRequestEnabled"
          type="switch"
          isDisabled={!canToggle}
          onChange={handleSwitchChange}
          data-testid={suffixTestId('tireSetRequest', props)}
        />
      </Box>
      <Show when={isEnabled}>
        <HStack spacing={4}>
          <Box flex={1}>
            <FormField
              control={props.control}
              label={i18n.t('entity.tiresInventory.labels.branch')}
              name="destinationBranchId"
              type="choice"
              options={destinationBranchesOptions}
              isLoading={isDestinationBranchesLoading}
              isRequired
              isDisabled={!canEditFields}
              isNotClearable
              onChange={handleBranchChange}
              data-testid={suffixTestId('requestedBranchId', props)}
            />
          </Box>
          <Box flex={1}>
            <HStack spacing={4} align="flex-end">
              <FormField
                control={props.control}
                label={i18n.t('entity.tiresInventory.labels.neededAt')}
                isRequired
                name="neededAtDate"
                type="apiDate"
                isDisabled={!canEditFields}
                disabledDates={getDisabledDates()}
                minDate={startOfDay(new Date())}
                data-testid={suffixTestId('neededAtDate', props)}
              />
              <FormField
                control={props.control}
                name="neededAtTime"
                isRequired
                type="time"
                isDisabled={!canEditFields}
                data-testid={suffixTestId('neededAtTime', props)}
              />
            </HStack>
          </Box>
          <Box flex={2}>
            <FormField
              control={props.control}
              label={i18n.t('general.labels.note')}
              name="requestNote"
              type="textarea"
              isDisabled={!canEditFields}
              data-testid={suffixTestId('note', props)}
            />
          </Box>
        </HStack>
      </Show>
    </VStack>
  );
}
